import React, { useMemo } from 'react';
import { mobileUiOchags } from '../../../utils/mobileCalculations';

const OchagCardsSection = ({
  form4Data = null,
  form6Data = null,
  handbooks = [],
  independentForm2Data = null,
  form1Data = null,
  dataType = 'to',
  formData = null,
  getOchagDamageValueForCause = null
}) => {

  // Формируем список причин с очагами
  const causesWithOchag = useMemo(() =>
    mobileUiOchags(4, form4Data, handbooks, independentForm2Data, form1Data, dataType),
    [form4Data, handbooks, independentForm2Data, form1Data, dataType]
  );

  // Получаем выбранные очаги из formData.selectedOchags (локальные данные из формы №4)
  const selectedOchags = useMemo(() => {
    if (formData?.selectedOchags === undefined) {
      return new Set();
    }
    return new Set(formData.selectedOchags || []);
  }, [formData, formData?.selectedOchags]);

  // Фильтруем очаги по выбранным в форме №4 (используем только локальные данные)
  const filteredCausesWithOchag = useMemo(() => {
    
    // Если selectedOchags существует в formData (даже если пустой массив), значит пользователь явно управлял выбором
    // В этом случае показываем только выбранные очаги (0, если все сняты)
    if (formData?.selectedOchags !== undefined) {
      // Фильтруем только те очаги, которые выбраны в форме №4
      const filtered = causesWithOchag.filter((cause, index) => {
        const causeKey = `${cause.id}-${index}`;
        // Проверяем, выбран ли очаг по текущему causeKey или по любому ключу с таким же ID
        const isSelected = selectedOchags.has(String(cause.id)) || selectedOchags.has(causeKey) ||
          Array.from(selectedOchags).some(key => key.startsWith(`${cause.id}-`));
        return isSelected;
      });
      return filtered;
    }
    
    // Если selectedOchags нет в formData, значит это первая загрузка - показываем все очаги
    return causesWithOchag;
  }, [causesWithOchag, selectedOchags, formData?.selectedOchags]);

  // Получаем данные очагов из formData.ochagData (точно как в форме №4)
  // Используем те же данные, что и форма №4 для синхронизации
  // ВАЖНО: используем formData.ochagData напрямую, чтобы всегда получать актуальные данные
  const ochagData = useMemo(() => {
    if (!formData || !formData.ochagData) {
      return {};
    }
    // Возвращаем данные напрямую из formData.ochagData
    return formData.ochagData;
  }, [formData?.ochagData]);

  if (filteredCausesWithOchag.length === 0) {
    return null;
  }

  return (
    <div style={{ 
      marginTop: '20px', 
      padding: '20px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #dee2e6'
    }}>
      <h3 style={{ 
        margin: '0 0 20px 0', 
        fontSize: '16px', 
        fontWeight: '600',
        color: '#212529'
      }}>
        Очаги
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '15px'
      }}>
        {filteredCausesWithOchag.map((cause, index) => {
          // Находим данные очага по коду причины (точно как в Form4)
          // ВАЖНО: используем formData.ochagData напрямую, чтобы всегда получать актуальные данные
          const causeCode = cause.code ? String(cause.code) : null;
          const currentOchagData = formData?.ochagData || {};
          let ochagInfo = causeCode ? (currentOchagData[causeCode] || {}) : {};
          
          // Если не нашли по коду, пробуем найти по ключу с ID (для обратной совместимости)
          if (Object.keys(ochagInfo).length === 0) {
            const ochagDataKey = Array.from(Object.keys(currentOchagData)).find(key => key.startsWith(`${cause.id}-`));
            if (ochagDataKey) {
              ochagInfo = currentOchagData[ochagDataKey] || {};
            }
          }

          const causeKey = `${cause.id}-${index}`;
          const isSelected = selectedOchags.has(String(cause.id)) || selectedOchags.has(causeKey) ||
            Array.from(selectedOchags).some(key => key.startsWith(`${cause.id}-`));

          return (
            <div
              key={causeKey}
              style={{
                backgroundColor: 'white',
                padding: '15px',
                borderRadius: '6px',
                border: '1px solid #dee2e6',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                position: 'relative'
              }}
            >
              <div style={{ 
                marginBottom: '10px'
              }}>
                <strong style={{ 
                  fontSize: '14px', 
                  color: '#212529',
                  display: 'block',
                  marginBottom: '10px'
                }}>
                  {cause.name}
                </strong>
                <div style={{ 
                  fontSize: '13px', 
                  color: '#495057',
                  fontWeight: '500',
                  marginBottom: '5px'
                }}>
                  Степень: <span style={{ 
                    fontWeight: '600'
                  }}>
                    {cause.state}
                  </span>
                </div>
                {/* Показываем "Доля повр. деревьев" для формы №4 */}
                {getOchagDamageValueForCause && (
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#495057',
                    fontWeight: '500',
                    marginBottom: '5px'
                  }}>
                    Доля повр. деревьев: <span style={{ 
                      fontWeight: '600'
                    }}>
                      {(() => {
                        const value = getOchagDamageValueForCause(cause.id);
                        return value ? `${value}%` : '-';
                      })()}
                    </span>
                  </div>
                )}
                {/* Отображаем данные очага: ВО всегда, фаза и тип только для главного */}
                {(ochagInfo.vo || ochagInfo.isMain) && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#6c757d',
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #e9ecef'
                  }}>
                    {ochagInfo.isMain && (
                      <div style={{ marginBottom: '4px' }}>
                        <strong>Главный очаг</strong>
                      </div>
                    )}
                    {ochagInfo.vo && (
                      <div style={{ marginBottom: '4px' }}>
                        ВО: {(() => {
                          const voHandbook = handbooks.find(h => h.id === 40);
                          if (voHandbook && voHandbook.references) {
                            const vo = voHandbook.references.find(
                              r => r.id.toString() === String(ochagInfo.vo)
                            );
                            return vo ? vo.name : ochagInfo.vo;
                          }
                          return ochagInfo.vo;
                        })()}
                      </div>
                    )}
                    {ochagInfo.isMain && ochagInfo.phase && (
                      <div style={{ marginBottom: '4px' }}>
                        Фаза: {(() => {
                          const phaseMap = {
                            'INITIAL': 'Начальная',
                            'POPULATION_GROWTH': 'Нарастания численности',
                            'OUTBREAK': 'Собственно вспышка',
                            'CRISIS': 'Кризис',
                            'EMERGING': 'Возникающий',
                            'ACTIVE': 'Действующий',
                            'FADING': 'Затухающий'
                          };
                          // Поддержка старых значений для обратной совместимости
                          if (ochagInfo.phase === 'arising') return 'Возникающая';
                          if (ochagInfo.phase === 'acting') return 'Действующая';
                          return phaseMap[ochagInfo.phase] || ochagInfo.phase;
                        })()}
                      </div>
                    )}
                    {ochagInfo.isMain && ochagInfo.type && (
                      <div style={{ marginBottom: '4px' }}>
                        Тип: {ochagInfo.type === 'episodic' ? 'Эпизодический' : 'Хронический'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OchagCardsSection;

